import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Car, Plus, Images, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Capacity {
  current: number;
  max: number;
  oldestArchivable: { id: string; stockNumber: string | null } | null;
}

export function DashboardPage() {
  const user = useAuth((s) => s.user)!;
  const capacity = useQuery({
    queryKey: ['capacity'],
    queryFn: () => api.get<Capacity>('/cars/capacity'),
  });

  const active = user.memberships.find((m) => m.tenantId === user.tenantId);
  const pct = capacity.data ? Math.round((capacity.data.current / capacity.data.max) * 100) : 0;

  return (
    <div className="container py-8 space-y-8">
      <header className="space-y-1">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {active?.tenantName ?? 'BGSwap'}
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Welcome back, {user.name.split(' ')[0]}.</h1>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active cars</CardDescription>
            <CardTitle className="text-3xl">{capacity.data?.current ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={pct} />
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>{pct}% of cap</span>
              <span>{capacity.data ? `${capacity.data.current}/${capacity.data.max}` : ''}</span>
            </div>
          </CardContent>
        </Card>
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <CardDescription>Start a new car</CardDescription>
            <CardTitle className="text-lg">Shoot a vehicle</CardTitle>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full gap-2">
              <Link to="/app/cars/new"><Plus className="h-4 w-4" /> New car session</Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Assets</CardDescription>
            <CardTitle className="text-lg">Backgrounds & overlays</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild variant="outline" className="w-full gap-2 justify-start">
              <Link to="/app/backgrounds"><Images className="h-4 w-4" /> Backgrounds</Link>
            </Button>
            <Button asChild variant="outline" className="w-full gap-2 justify-start">
              <Link to="/app/overlays"><Upload className="h-4 w-4" /> Overlays</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {capacity.data && capacity.data.current >= capacity.data.max && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="flex items-center justify-between gap-4 py-4 text-sm">
            <div>
              <Badge variant="warning" className="mr-2">Capacity reached</Badge>
              You've hit the {capacity.data.max}-car cap. Archive the oldest car to continue.
            </div>
            <Button asChild size="sm">
              <Link to="/app/cars">Manage cars</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <section>
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <Car className="h-4 w-4" /> Quick tips
        </h2>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>· Use the phone's rear camera and the green alignment guide for consistent shots.</li>
          <li>· Retake any shot in-place — previous version is discarded.</li>
          <li>· Backgrounds and overlays can be re-applied later without re-shooting.</li>
        </ul>
      </section>
    </div>
  );
}
